/**
 * O que foi mapeado do contrato (`TP-067`): regras vigentes, cláusulas e as contagens do Resumo.
 *
 * Forma crua da API, tradução e chamadas no mesmo arquivo: é um bloco só de leitura, e separar em
 * `tipos.ts`/`mapeadores.ts`/`contratos.ts` espalharia três rotas por três arquivos. Os tipos `*Api`
 * espelham os schemas `ContractRule`, `ContractClause` e `ContractSummary` de
 * `contracts/openapi/anery-api.v1.json`.
 */

import { queryOptions } from '@tanstack/react-query';

import { requisitar } from '../../../shared/api/clienteHttp';
import { CONFIANCA, TIPO_DE_REGRA } from '../revisao/api/mapeadores';
import type { ConfiancaDaRegra, ConfiancaDaRegraApi, TipoDeRegra, TipoDeRegraApi } from '../revisao/api/tipos';

// ------------------------------------------------------------- forma da API

export interface RegraDoContratoApi {
  readonly id: string;
  readonly sourceId: string;
  readonly type: TipoDeRegraApi;
  readonly confidence: ConfiancaDaRegraApi;
  readonly excerpt: string | null;
  readonly page: number;
  readonly pageEnd: number | null;
  readonly clause: { readonly id: string; readonly number: string | null; readonly title: string | null } | null;
  readonly glosas: { readonly count: number; readonly amountDenied: number } | null;
  readonly whatItDoes?: string | null;
  readonly section?: string | null;
}

export interface ClausulaDoContratoApi {
  readonly id: string;
  readonly sourceId: string;
  readonly number: string | null;
  readonly title: string | null;
  readonly pageStart: number;
  readonly pageEnd: number;
  readonly parentId: string | null;
  readonly text: string | null;
  readonly activeRules: number;
  readonly section?: string | null;
}

export interface ResumoDoContratoApi {
  readonly pages: number;
  readonly pagesByContentType: readonly { readonly contentType: string | null; readonly pages: number }[] | null;
  readonly rulesByType: readonly { readonly type: TipoDeRegraApi; readonly active: number; readonly pending: number }[] | null;
  readonly rulesOutsideClause: number;
  readonly clauses: number;
  readonly items: { readonly total: number; readonly safe: number; readonly reviewed: number; readonly notReviewed: number };
}

// -------------------------------------------------------- domínio do front

export interface RegraDoContrato {
  readonly id: string;
  readonly fonteId: string;
  readonly tipo: TipoDeRegra;
  readonly confianca: ConfiancaDaRegra;
  readonly trecho: string;
  readonly pagina: number;
  readonly paginaFim: number | null;
  /** `null` = regra fora de cláusula numerada (as do Anexo). */
  readonly clausula: { readonly id: string; readonly numero: string; readonly titulo: string | null } | null;
  /** Só na hipótese de glosa: quantas linhas de glosa a citaram e quanto foi glosado. */
  readonly glosas: { readonly quantidade: number; readonly valorGlosado: number } | null;
  /** O que a regra faz, numa frase montada pela API ("Comunicado: até 24 h úteis"); `null` = só o trecho diz. */
  readonly oQueFaz: string | null;
  /** A seção do documento na página da regra ("Anexo I – …"), lida pelo mapa; `null` se o mapa não deu. */
  readonly secao: string | null;
}

export interface ClausulaDoContrato {
  readonly id: string;
  readonly fonteId: string;
  readonly numero: string;
  readonly titulo: string | null;
  readonly paginaInicio: number;
  readonly paginaFim: number;
  readonly texto: string;
  readonly regrasVigentes: number;
  /** A seção do documento onde a cláusula começa ("Anexo I – …"); `null` se o mapa não deu. */
  readonly secao: string | null;
}

export interface ResumoDoContrato {
  readonly paginas: number;
  readonly paginasPorConteudo: readonly { readonly conteudo: string; readonly paginas: number }[];
  readonly regrasPorTipo: readonly { readonly tipo: TipoDeRegra; readonly vigentes: number; readonly aRevisar: number }[];
  readonly regrasForaDeClausula: number;
  readonly clausulas: number;
  readonly itens: { readonly total: number; readonly seguros: number; readonly conferidos: number; readonly naoConferidos: number };
}

/**
 * Os grupos da aba Regras, na ordem em que a operação lê um contrato (`D5`): o que está dentro da
 * diária, o que se cobra à parte, o que pede autorização, como se cobra e como se calcula, os prazos e,
 * por fim, a glosa. Prazo documental e SLA ficam juntos: para quem lê, os dois são "prazos".
 */
export const GRUPOS_DE_REGRA: readonly { readonly chave: string; readonly titulo: string; readonly tipos: readonly TipoDeRegra[] }[] = [
  { chave: 'inclusao', titulo: 'O que já está na diária', tipos: ['inclusao'] },
  { chave: 'exclusao', titulo: 'O que se cobra à parte', tipos: ['exclusao'] },
  { chave: 'autorizacao', titulo: 'O que precisa de autorização', tipos: ['autorizacao'] },
  { chave: 'cobranca', titulo: 'Como se cobra', tipos: ['regra_cobranca', 'unidade_cobranca'] },
  { chave: 'formula', titulo: 'Como se calcula o preço fora da tabela', tipos: ['formula_preco'] },
  { chave: 'prazos', titulo: 'Prazos', tipos: ['prazo_documental', 'sla'] },
  { chave: 'glosa', titulo: 'O que pode ser glosado', tipos: ['hipotese_glosa'] },
  { chave: 'recurso', titulo: 'Prazo para recorrer de glosa', tipos: ['prazo_recurso'] },
  { chave: 'nao-classificavel', titulo: 'Não classificáveis', tipos: ['nao_classificavel'] },
];

/** O rótulo do tipo de conteúdo de página que o mapa (passo 2 da leitura) usa. Token novo aparece cru. */
const ROTULOS_DE_CONTEUDO: Readonly<Record<string, string>> = {
  tabela_preco: 'tabela de preço',
  composicao: 'composição',
  autorizacao: 'autorização',
  exclusao: 'exclusão',
  inclusao: 'inclusão',
  sla: 'SLA',
  lgpd: 'LGPD',
  assinatura: 'assinatura',
  glosa: 'glosa',
  prazo: 'prazo',
  cobranca: 'cobrança',
  reajuste: 'reajuste',
};

/** A tela de Glosas filtrada pela hipótese (`TP-067`, `D6`). */
export function caminhoDasGlosasDaHipotese(regraId: string): string {
  return `/glosa-e-recurso?${new URLSearchParams({ hipoteseRegraId: regraId }).toString()}`;
}

/** "Cláusula 8.4 — DO PRAZO" ou "fora de cláusula numerada": de onde a regra saiu, ao lado da página. */
export function origemDaRegra(regra: RegraDoContrato): string {
  if (regra.clausula === null) return 'fora de cláusula numerada';
  return regra.clausula.titulo !== null && regra.clausula.titulo.trim() !== ''
    ? `cláusula ${regra.clausula.numero} — ${regra.clausula.titulo}`
    : `cláusula ${regra.clausula.numero}`;
}

export function rotuloDoConteudo(token: string): string {
  return ROTULOS_DE_CONTEUDO[token] ?? token.replaceAll('_', ' ');
}

// ------------------------------------------------------------- tradução

function textoOuNulo(valor: string | null | undefined): string | null {
  return valor === undefined || valor === null || valor.trim() === '' ? null : valor.trim();
}

function mapearRegra(bruto: RegraDoContratoApi): RegraDoContrato {
  return {
    id: bruto.id,
    fonteId: bruto.sourceId,
    tipo: TIPO_DE_REGRA[bruto.type],
    confianca: CONFIANCA[bruto.confidence],
    trecho: bruto.excerpt ?? '',
    pagina: bruto.page,
    paginaFim: bruto.pageEnd,
    clausula:
      bruto.clause === null ? null : { id: bruto.clause.id, numero: bruto.clause.number ?? '—', titulo: bruto.clause.title },
    glosas: bruto.glosas === null ? null : { quantidade: bruto.glosas.count, valorGlosado: bruto.glosas.amountDenied },
    oQueFaz: bruto.whatItDoes ?? null,
    secao: textoOuNulo(bruto.section),
  };
}

function mapearClausula(bruto: ClausulaDoContratoApi): ClausulaDoContrato {
  return {
    id: bruto.id,
    fonteId: bruto.sourceId,
    numero: bruto.number ?? '—',
    titulo: bruto.title,
    paginaInicio: bruto.pageStart,
    paginaFim: bruto.pageEnd,
    texto: bruto.text ?? '',
    regrasVigentes: bruto.activeRules,
    secao: textoOuNulo(bruto.section),
  };
}

function mapearResumo(bruto: ResumoDoContratoApi): ResumoDoContrato {
  return {
    paginas: bruto.pages,
    paginasPorConteudo: (bruto.pagesByContentType ?? []).map((c) => ({ conteudo: c.contentType ?? '', paginas: c.pages })),
    regrasPorTipo: (bruto.rulesByType ?? []).map((r) => ({ tipo: TIPO_DE_REGRA[r.type], vigentes: r.active, aRevisar: r.pending })),
    regrasForaDeClausula: bruto.rulesOutsideClause,
    clausulas: bruto.clauses,
    itens: {
      total: bruto.items.total,
      seguros: bruto.items.safe,
      conferidos: bruto.items.reviewed,
      naoConferidos: bruto.items.notReviewed,
    },
  };
}

// ------------------------------------------------------------- chamadas

export const chavesDoMapeamento = {
  raiz: ['contratos', 'mapeamento'] as const,
  regras: (contratoId: string, competencia: string) => [...chavesDoMapeamento.raiz, contratoId, 'regras', competencia] as const,
  clausulas: (contratoId: string, competencia: string) =>
    [...chavesDoMapeamento.raiz, contratoId, 'clausulas', competencia] as const,
  resumo: (contratoId: string, competencia: string) => [...chavesDoMapeamento.raiz, contratoId, 'resumo', competencia] as const,
};

function caminho(contratoId: string, recurso: string, competencia: string): string {
  return `/contratos/${encodeURIComponent(contratoId)}/${recurso}?${new URLSearchParams({ competencia }).toString()}`;
}

export function opcoesDasRegras(contratoId: string, competencia: string) {
  return queryOptions({
    queryKey: chavesDoMapeamento.regras(contratoId, competencia),
    queryFn: ({ signal }): Promise<RegraDoContrato[]> =>
      requisitar<RegraDoContratoApi[]>(caminho(contratoId, 'regras', competencia), { sinal: signal }).then((lista) =>
        lista.map(mapearRegra),
      ),
    enabled: contratoId !== '' && competencia.trim() !== '',
  });
}

export function opcoesDasClausulas(contratoId: string, competencia: string) {
  return queryOptions({
    queryKey: chavesDoMapeamento.clausulas(contratoId, competencia),
    queryFn: ({ signal }): Promise<ClausulaDoContrato[]> =>
      requisitar<ClausulaDoContratoApi[]>(caminho(contratoId, 'clausulas', competencia), { sinal: signal }).then((lista) =>
        lista.map(mapearClausula),
      ),
    enabled: contratoId !== '' && competencia.trim() !== '',
  });
}

export function opcoesDoResumo(contratoId: string, competencia: string) {
  return queryOptions({
    queryKey: chavesDoMapeamento.resumo(contratoId, competencia),
    queryFn: ({ signal }): Promise<ResumoDoContrato> =>
      requisitar<ResumoDoContratoApi>(caminho(contratoId, 'resumo', competencia), { sinal: signal }).then(mapearResumo),
    enabled: contratoId !== '' && competencia.trim() !== '',
  });
}
